import { useSuspenseQuery, type MutationFunction } from "@tanstack/react-query";
import { Flex, PrimarySecondaryPage } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import { OnboardingStepper } from "onboarding";

import { ManagementAccountsUploadSupportingContent } from "../../../components";
import {
  useTriggerValidation,
  useValidatedDocumentQuery,
} from "../../../queries/document-uploads/validated-documents-queries";
import {
  useFundingApplication,
  useFundingApplicationMutation,
  type FormattedFundingApplicationScreenUpdate,
} from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";
import { DocumentNavigationGroup } from "../components";
import { ProfitAndLossDocumentUpload } from "../components/profit-and-loss-document-upload";
import { getAccountingThreshold } from "../../../utils/funding-journey";
import { getCustomerBusinessModelQueryOptions } from "../../../queries/customer-data/queries";

export const ProfitAndLossDocumentUploadPage = () => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplicationData } = useFundingApplication(customerDomain);
  const { data: businessModel } = useSuspenseQuery(getCustomerBusinessModelQueryOptions(customerDomain));
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { data: uploadedDocuments } = useValidatedDocumentQuery(customerDomain, fundingApplicationData.id);
  const { mutate: triggerValidation } = useTriggerValidation(
    customerDomain,
    fundingApplicationData.id,
    UploadDocumentType.PROFIT_AND_LOSS
  );

  const filteredDocumentTypes = uploadedDocuments?.filter(
    (doc) => doc.documentType === UploadDocumentType.PROFIT_AND_LOSS
  );
  const filteredFiles = filteredDocumentTypes?.flatMap(({ data }) => data);
  const hasUploadedDocuments = Boolean(filteredFiles?.length);

  const primaryContent = (
    <Flex direction="column" gap="6">
      <ProfitAndLossDocumentUpload
        fundingApplication={fundingApplicationData}
        mutate={fundingApplicationMutation.mutate as MutationFunction<FormattedFundingApplicationScreenUpdate>}
        accountingThreshold={getAccountingThreshold(businessModel)}
        hasUploadedDocuments={hasUploadedDocuments}
      />
      <DocumentNavigationGroup
        isStepComplete={
          fundingApplicationMutation.variables?.accountingOptedOut ||
          fundingApplicationData.accountingOptedOut ||
          (hasUploadedDocuments && Boolean(fundingApplicationData.accountingCurrency))
        }
        forType={UploadDocumentType.PROFIT_AND_LOSS}
        onPrimaryAction={() => filteredFiles?.length && triggerValidation(filteredFiles.map(({ id }) => id))}
      />
    </Flex>
  );

  return (
    <PrimarySecondaryPage
      titleSlot={<OnboardingStepper currentStep={4} />}
      showStepperBackground={true}
      title={intl.formatMessage({
        id: "funding-application: upload profit and loss",
        defaultMessage: "Upload your Profit and Loss Statement",
      })}
      primaryContent={primaryContent}
      secondaryContent={<ManagementAccountsUploadSupportingContent />}
    />
  );
};
